<script setup lang="ts">
import { iRadio } from '@inscribe/signal';
import type { ConfigurableInsightLayoutType } from '@/types/decision-engine/configurable-insights.types';
import { INSIGHT_TYPE_OPTIONS } from '@/V2/decision-engine/configurable-insights/filter-constants';

interface Props {
  modelValue: ConfigurableInsightLayoutType;
  disabled?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: ConfigurableInsightLayoutType): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const options = INSIGHT_TYPE_OPTIONS;

const onOptionSelected = (value: ConfigurableInsightLayoutType) => {
  if (props.disabled) return;
  emit('update:modelValue', value);
};
</script>

<template>
  <div class="insight-type-selector" :class="{ disabled: props.disabled }">
    <i-radio
      :options="options"
      :default-value="modelValue"
      :disabled="props.disabled"
      @option-selected="onOptionSelected"
    />
  </div>
</template>

<style scoped lang="scss">
.insight-type-selector {
  &.disabled {
    cursor: not-allowed;

    :deep(.option) {
      border: 1px solid var(--border-neutral-subtle);

      input {
        background: var(--background-neutral-subtle);
      }

      .label span {
        color: var(--text-neutral-subtle);
      }
    }
  }

  // customisation for the radio button overriding design system defaults
  :deep(.i-radio-checkbox) {
    display: flex;
    flex-direction: row;
    gap: 12px; // need to specify here as overrides design system default
    height: 118px;

    .option {
      height: 100%;
      width: 100%;
      padding: 24px;
      display: flex;
      flex-direction: column;
      align-items: start;
      background: var(--base);
      border: 1px solid var(--border-neutral-subtle);
      transition: all 0.15s ease-in-out;
      border-radius: 20px;

      &:hover {
        border: 1px solid var(--border-neutral-bold);
      }

      &.checked {
        background: var(--surface-neutral-subtle);
        border: 1px solid var(--border-neutral-bold);
      }

      input {
        min-width: 16px;
        min-height: 16px;
      }

      .label {
        height: 100%;
        gap: 2px;
      }
    }
  }
}
</style>
